import React, { useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import ThumbnailPicker from './thumbnails/ThumbnailPicker';
import useVideoThumbnail from './thumbnails/useVideoThumbnail';
import { selectThumbnail } from './thumbnails/thumbnailFiles';

export default function VideoThumbnailField({
  kind,
  videoId,
  title,
  value,
  onChange,
  disabled,
  onBusyChange,
}) {
  const saved = useVideoThumbnail(kind, videoId);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const working = useRef(false);
  const thumbnail =
    value === undefined
      ? saved.thumbnail
      : value
        ? { fileName: value.name, blob: value }
        : null;
  async function choose(files) {
    if (working.current || disabled) return;
    working.current = true;
    setValidating(true);
    onBusyChange?.(true);
    setError('');
    try {
      onChange(await selectThumbnail(files));
    } catch (err) {
      setError(err.message);
    } finally {
      working.current = false;
      setValidating(false);
      onBusyChange?.(false);
    }
  }
  return (
    <Box
      component="section"
      aria-label="Video thumbnail"
      sx={{
        my: 2,
        p: 2,
        border: '1px solid #dce1f4',
        borderRadius: 2,
        bgcolor: '#fff',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
        Video thumbnail{' '}
        <Typography component="span" variant="caption" color="text.secondary">
          · Optional
        </Typography>
      </Typography>
      <ThumbnailPicker
        loading={saved.loading}
        thumbnail={thumbnail}
        title={title}
        onFiles={choose}
        onRemove={() => {
          onChange(null);
          setError('');
        }}
        busy={validating}
        disabled={disabled || saved.loading}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1.5 }}
      >
        {videoId
          ? 'Thumbnail changes apply when you save. An image already on YouTube stays there if you remove this attachment.'
          : 'Saved with your new video and included in its YouTube upload.'}
      </Typography>
      {(error || saved.error) && (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          action={
            saved.error ? (
              <Button onClick={saved.reload}>Reload</Button>
            ) : undefined
          }
        >
          {error || saved.error}
        </Alert>
      )}
    </Box>
  );
}
